import React, { useState, useMemo } from 'react'
import { Play, Clock, Check } from 'lucide-react'

const EpisodeSelector = ({ 
  episodes, 
  currentEpisode, 
  onEpisodeSelect, 
  watchHistory 
}) => {
  const [selectedSeason, setSelectedSeason] = useState(1)

  // Group episodes by season
  const episodesBySeason = useMemo(() => {
    if (!episodes || episodes.length === 0) return {}
    
    return episodes.reduce((acc, episode) => {
      const season = episode.season_number || 1
      if (!acc[season]) {
        acc[season] = []
      }
      acc[season].push(episode)
      return acc
    }, {})
  }, [episodes])

  const seasons = Object.keys(episodesBySeason).map(Number).sort((a, b) => a - b)
  const currentSeasonEpisodes = episodesBySeason[selectedSeason] || []

  if (!episodes || episodes.length === 0) {
    return null
  }

  const formatDuration = (seconds) => {
    if (!seconds) return '0:00'
    const hours = Math.floor(seconds / 3600)
    const minutes = Math.floor((seconds % 3600) / 60)
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`
    }
    return `${minutes}m`
  }

  const getEpisodeProgress = (episode) => {
    if (!watchHistory || !Array.isArray(watchHistory)) return 0
    
    const episodeHistory = watchHistory.find(h => h.episode_id === episode.id)
    if (!episodeHistory) return 0
    
    return Math.min((episodeHistory.watch_duration_seconds / episode.duration_seconds) * 100, 100)
  }

  const isEpisodeCompleted = (episode) => {
    const progress = getEpisodeProgress(episode)
    return progress >= 90
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Episodes</h3>
        
        {/* Season Selector */}
        {seasons.length > 1 && (
          <select
            value={selectedSeason}
            onChange={(e) => setSelectedSeason(Number(e.target.value))}
            className="px-3 py-1 bg-secondary text-secondary-foreground rounded border border-muted text-sm"
          >
            {seasons.map(season => (
              <option key={season} value={season}>
                Season {season}
              </option>
            ))}
          </select>
        )}
      </div>

      {/* Episodes List */}
      <div className="space-y-3">
        {currentSeasonEpisodes.map((episode, index) => {
          const isSelected = currentEpisode?.id === episode.id
          const progress = getEpisodeProgress(episode)
          const isCompleted = isEpisodeCompleted(episode)
          
          return (
            <div
              key={episode.id}
              onClick={() => onEpisodeSelect(episode)}
              className={`p-4 rounded-lg border cursor-pointer transition-all hover:border-primary ${
                isSelected 
                  ? 'border-primary bg-primary/5' 
                  : 'border-muted bg-card hover:bg-accent'
              }`}
            >
              <div className="flex space-x-4">
                {/* Episode Thumbnail */}
                <div className="relative flex-shrink-0">
                  {episode.thumbnail_url ? (
                    <img
                      src={episode.thumbnail_url}
                      alt={`Episode ${episode.episode_number}`}
                      className="w-24 h-16 object-cover rounded bg-muted"
                    />
                  ) : (
                    <div
                      className="w-24 h-16 rounded bg-gradient-to-br from-muted to-muted/40 flex items-center justify-center"
                      aria-label={`Episode ${episode.episode_number}`}
                    >
                      <Play className="w-5 h-5 text-muted-foreground" />
                    </div>
                  )}
                  
                  {/* Play Button Overlay */}
                  <div className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 hover:opacity-100 transition-opacity rounded">
                    <Play className="w-6 h-6 text-white" fill="currentColor" />
                  </div>
                  
                  {/* Progress Bar */}
                  {progress > 0 && (
                    <div className="absolute bottom-0 left-0 right-0 h-1 bg-black/50 rounded-b">
                      <div 
                        className="h-full bg-primary rounded-b transition-all duration-300"
                        style={{ width: `${progress}%` }}
                      ></div>
                    </div>
                  )}
                  
                  {/* Completed Badge */}
                  {isCompleted && (
                    <div className="absolute top-1 right-1 bg-primary text-primary-foreground rounded-full p-1">
                      <Check className="w-3 h-3" />
                    </div>
                  )}
                </div>

                {/* Episode Info */}
                <div className="flex-1 min-w-0 space-y-1">
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium truncate">
                      {episode.episode_number}. {episode.title}
                    </h4>
                    <div className="flex items-center space-x-2 text-xs text-muted-foreground">
                      <Clock className="w-3 h-3" />
                      <span>{formatDuration(episode.duration_seconds)}</span>
                    </div>
                  </div>
                  
                  <p className="text-sm text-muted-foreground line-clamp-2">
                    {episode.description || 'No description available.'}
                  </p>
                  
                  {episode.air_date && (
                    <p className="text-xs text-muted-foreground">
                      Aired: {new Date(episode.air_date).toLocaleDateString()}
                    </p>
                  )}
                  
                  {/* Progress Text */}
                  {progress > 0 && progress < 90 && (
                    <p className="text-xs text-primary">
                      {Math.floor(progress)}% watched
                    </p>
                  )}
                  
                  {isCompleted && (
                    <p className="text-xs text-primary flex items-center space-x-1">
                      <Check className="w-3 h-3" />
                      <span>Completed</span>
                    </p>
                  )}
                </div>
              </div>
            </div>
          )
        })}
      </div>

      {/* Season Summary */}
      {seasons.length > 1 && (
        <div className="text-sm text-muted-foreground text-center pt-2 border-t">
          Season {selectedSeason} • {currentSeasonEpisodes.length} episodes
        </div>
      )}
    </div>
  )
}

export default EpisodeSelector